import React, { useMemo, useEffect, useState, Suspense } from 'react';
import { useLocation } from 'react-router-dom';
import { AppLayout } from './AppLayout';
import { ErpLayout } from '../../features/erp/ErpLayout';
import { RouteLoading } from '../widgets/loading';
import { ROUTE_REGISTRY } from '../../config/routes';

const ErpPage = React.lazy(() => import('../../features/erp/ErpPage'));

export const AppRouter = () => {
    const location = useLocation();
    const path = location.pathname;
    const isErpPath = path.startsWith('/erp');

    const [visitedRoutes, setVisitedRoutes] = useState<Set<string>>(new Set());

    const activeKey = useMemo(() => {
        const found = ROUTE_REGISTRY.find(route => route.matcher(path));
        return found ? found.key : null;
    }, [path]);

    // Don't track ERP in the AppLayout visited-routes cache — it uses its own layout
    useEffect(() => {
        if (activeKey && activeKey !== 'erp' && !visitedRoutes.has(activeKey)) {
            setVisitedRoutes(prev => {
                const updated = new Set(prev);
                updated.add(activeKey);
                return updated;
            });
        }
    }, [activeKey, visitedRoutes]);

    // Clear visited routes cache when auth state changes to ensure fresh data/state
    useEffect(() => {
        const handleAuthChange = () => {
            setVisitedRoutes(new Set());
        };
        window.addEventListener('authStateChange', handleAuthChange);
        return () => window.removeEventListener('authStateChange', handleAuthChange);
    }, []);

    // Pre-compute non-ERP route elements (all hooks must be called unconditionally)
    const routeElements = useMemo(() => ROUTE_REGISTRY
        .filter(r => r.key !== 'erp')
        .map((route) => {
            const isActive = activeKey === route.key;
            const isVisited = visitedRoutes.has(route.key);

            if (!isVisited) return null;

            return (
                <div
                    key={route.key}
                    style={{ display: isActive ? 'block' : 'none', height: '100%' }}
                >
                    <route.component />
                </div>
            );
        }), [activeKey, visitedRoutes]);

    if (isErpPath) {
        return (
            <Suspense fallback={<RouteLoading />}>
                <ErpLayout>
                    <ErpPage />
                </ErpLayout>
            </Suspense>
        );
    }

    return (
        <AppLayout>
            <Suspense fallback={<RouteLoading />}>
                {routeElements}
            </Suspense>
        </AppLayout>
    );
};
